﻿import React, { useEffect, Fragment, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types';
import './Layout.css';
import { AccountService, AlertService, UserService, EmployeeService, AssetService } from 'services';
import { ButtonList } from 'components/button-list';
import { Menu, MenuItem, MenuDivider, Icon, Intent, Tag } from "@blueprintjs/core";
import { InternalMenuItem } from 'components/navigation';
import { AccountSwitcher } from 'components/notifications/AccountSwitcher';
import { UserAccess, UserHasAccess } from 'components/user-access/UserAccess';
import { Button } from 'components/buttons/Button';
import { FormSuggest } from 'components/form-fields/FormSuggest';
import { ButtonSave } from 'components/buttons/ButtonSave';
import axios from 'axios';
import { NotificationToaster } from 'components/notifications/NotificationToaster';
import { NotificationInline } from 'components/notifications';
import classNames from 'classnames';
import { ShowHide } from './ShowHide';
import { CustomIcon } from 'components/icon/CustomIcon';

const defaultFunction = () => {};

export function TopBar(props) {

    const dispatch = useDispatch();
    const onLeaveFeedback = props.onLeaveFeedback ?? defaultFunction;
    const onReportIssue = props.onReportIssue ?? defaultFunction;
    const displayName = useSelector(state => state.loggedInUser.displayName);
    const preventSwitch = useSelector(state => state.dirtyData.dirty);
    const tryingToSwitch = useSelector(state => state.dirtyData.attemptingRedirect);
    const allowDirtySwitch = useSelector(state => state.dirtyData.allowRedirect);
    const dirtyTask = useSelector(state => state.dirtyData.task);
    const accountName = useSelector(state => state.loggedInUser.memberName);
    const memberNumber = useSelector(state => state.loggedInUser.memberNumber);

    const sidebarColour = useSelector(state => state.theme.sidebarColour);
    const sidebarColourPreview = useSelector(state => state.theme.sidebarColourPreview);
    const sidebarTextColour = useSelector(state => state.theme.sidebarTextColour);
    const sidebarTextColourPreview = useSelector(state => state.theme.sidebarTextColourPreview);
    const sidebarActiveMenuColourPreview = useSelector(state => state.theme.sidebarActiveMenuColourPreview);
    const logoUploadedUrl = useSelector(state => state.theme.logoUploadedUrl);
    const logoUploadedPreview = useSelector(state => state.theme.logoUploadedPreview);
    const applicationName = useSelector(state => state.theme.applicationName);

    const isInternal = UserHasAccess("Internal");
    const [switchableAccounts, setSwitchableAccounts] = useState([]);
    const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(0);
    const [attemptingSwitch, setAttemptingSwitch] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [alertTypes, setAlertTypes] = useState([]);
    const [alertCount, setAlertCount] = useState(0);
    const [loadingAlerts, setLoadingAlerts] = useState(true);
    const [loadingEmployeeHoldingArea, setLoadingEmployeeHoldingArea] = useState(true);
    const [loadingAssetHoldingArea, setLoadingAssetHoldingArea] = useState(true);
    const [employeeHoldingAreaCount, setEmployeeHoldingAreaCount] = useState(0);
    const [assetHoldingAreaCount, setAssetHoldingAreaCount] = useState(0);
    const isDriver = UserHasAccess("AuthorisedDriver");
    const canSeeHomePage = UserHasAccess("HoldingArea:View");

    // logoUploadedPreview is now always a data-URL string (or null) — the reseller settings page
    // converts any uploaded File to a string before dispatching it. Derive the source directly:
    // prefer the live preview, then the saved logo, then the environment default.
    const logoSrc = logoUploadedPreview || logoUploadedUrl || (props.environment ? "/images/vision-logo-white.svg" : "https://vision.fta.co.uk/Content/images/svg/vision-logo.svg");

    const topBarStyleRef = useRef(null);

    const location = useLocation();

    useEffect(() => {
        dispatch({ type: 'SITE_DEFAULT_WIDTH' });
        dispatch({ type: 'RESET_THEME_PREVIEW' });
    }, [location.pathname]);


    const maxAlertCount = 999;

    useEffect(() => {
        axios.all([
            AccountService.getAllAccounts(undefined, undefined, true),
            UserService.getAllAccounts(isInternal || isDriver),
        ]).then(axios.spread(function (getAllAccountsReponse, accountAccessReponse) {

            let mappedAccounts = getAllAccountsReponse.data.map(function (account) {
                let accessText = '';
                const accountRestricted = account.restricted && isInternal;

                if (accountRestricted) {
                    if (account.restrictedAccessType === 'PARTIALACCESS') {
                        accessText = ' (Restricted for you)';
                    } else if (account.restrictedAccessType === 'NOACCESS') {
                        accessText = ' (Globally restricted)';
                    }
                }

                account.name = `${account.memberNumber} - ${account.name}${accessText}`;
                account.disabled = accountRestricted;
                return account;
            });

            if (accountAccessReponse) {
                if (!isInternal && accountAccessReponse.canSwitchAccount && !accountAccessReponse.switchAllAccounts) {
                    mappedAccounts = mappedAccounts.filter(c => accountAccessReponse.accountIds.find(a => a === c.id) != null);
                }
            }

            setSwitchableAccounts(mappedAccounts.filter(c => c.memberNumber !== memberNumber));

            setAccountsLoading(false);
        }));

        const alertsReloadTime = 300000; //5 * 60 * 1000 - 5 minutes

        loadAlerts();

        const alertBellInterval = setInterval(() => {
            loadAlerts();
        }, alertsReloadTime);
        return () => clearInterval(alertBellInterval);
    }, []);

    useEffect(() => {
        if (allowDirtySwitch === true && tryingToSwitch === true) {

            switch (dirtyTask) {

                case "SWITCHACCOUNT":
                    confirmAccountSwitch();
                    break;
                case "LOGOUT":
                    onLogout();
                    break;
                default:
                    break;
            }

        }
    }, [allowDirtySwitch, tryingToSwitch]);

    useEffect(() => {
        setAlertCount(alertTypes
            .map(x => x.alertCount)
            .reduce((partialSum, a) => partialSum + a, 0));
    }, [alertTypes])

    useEffect(() => {
        const styleId = 'dynamic-top-bar';
        let styleTag = topBarStyleRef.current;

        const bg = sidebarColourPreview || sidebarColour || "#0C2235";
        const textColor = sidebarTextColourPreview || sidebarTextColour || "#FFFFFF";
        const css = `
            ${isThemePreviewMode() || props.environment === "live" ?
                `
                #top-bar {
                    background: ${bg} !important;
                }
                ` : ''}
                #top-bar .bp6-button, #top-bar .bp6-icon, #top-bar .top-bar-item {
                    color: ${textColor} !important;
                }
        `;

        if (!styleTag) {
            styleTag = document.createElement('style');
            styleTag.id = styleId;
            document.head.appendChild(styleTag);
            topBarStyleRef.current = styleTag;
        }
        styleTag.innerHTML = css;

        return () => {
            removeTopBarStyles();
        };
    }, [sidebarColour, sidebarColourPreview, sidebarTextColour, sidebarTextColourPreview, sidebarActiveMenuColourPreview, props.environment]);

    function removeTopBarStyles() {
        if (topBarStyleRef.current) {
            document.head.removeChild(topBarStyleRef.current);
            topBarStyleRef.current = null;
        }
    }

    const isThemePreviewMode = useCallback(() => {
        return sidebarColourPreview || sidebarTextColourPreview || sidebarActiveMenuColourPreview || false;
    }, [sidebarColourPreview, sidebarTextColourPreview, sidebarActiveMenuColourPreview]);

    function onLogout() {
        if (preventSwitch === true && allowDirtySwitch !== true) {
            dispatch({ type: 'BEGIN_REDIRECT', payload: "LOGOUT" });
            return;
        }

        UserService.signOut();
    }

    function loadAlerts() {
        loadAlertBell();
        if (canSeeHomePage) {
            loadHoldingArea();
        }
    }

    function loadAlertBell() {
        AlertService.getAlertTypes(true, true)
            .then(newAlertTypes => {
                const formattedAlertTypes = newAlertTypes
                    .filter(x => x.alertCount > 0)
                    .map(x => formatAlertType(x));

                setAlertTypes(formattedAlertTypes);

                setLoadingAlerts(false);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not load your alert data. Please try again.");
            });
    }

    function loadHoldingArea() {
        setLoadingEmployeeHoldingArea(true);
        setLoadingAssetHoldingArea(true);

        EmployeeService.GetHoldingAreaCounts()
            .then(result => {
                setEmployeeHoldingAreaCount(result.count);
                setLoadingEmployeeHoldingArea(false);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not load your employee holding area data. Please try again.");
            });

        AssetService.GetHoldingAreaCounts()
            .then(result => {
                setAssetHoldingAreaCount(result.count);
                setLoadingAssetHoldingArea(false);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not load your asset holding area data. Please try again.");
            });
    }

    function formatAlertType(alertType) {
        const formattedAlertType = {
            ...alertType,
            formattedTitle: formatAlertTypeTitle(alertType)
        };

        if (alertType.storedInLegacySystem) {
            formattedAlertType.url = `${window.env.VISION_URL}#/Alerts/${alertType.alertTypeId}`;
        } else {
            formattedAlertType.url = `/alert/${alertType.key}`;
        }

        return formattedAlertType;
    }

    function showAccountSelector() {
        setShowAccountSwitcher(true);
    }

    function hideAccountSelector() {
        setShowAccountSwitcher(false);
    }

    function confirmAccountSwitch() {
        if (selectedAccount !== 0 && selectedAccount != null) {
            setAttemptingSwitch(true);

            UserService.switchAccount(selectedAccount).then(() => {
                hideAccountSelector();
                window.location.reload();
            },
                (error) => {
                    NotificationToaster.show(Intent.DANGER, "You do not have permission to switch to this account.");
                    setAttemptingSwitch(false);
                    hideAccountSelector();

                    const noAccessCode = 403;

                    if (!isInternal && error.status === noAccessCode) {
                        var mappedAccounts = switchableAccounts;
                        mappedAccounts = mappedAccounts.filter(c => c.id !== selectedAccount);
                        setSwitchableAccounts(mappedAccounts);
                    }
                });
        }
    }

    function onAccountSelect(item) {
        setSelectedAccount(item.id);
    }

    function formatAlertTypeTitle(alertType) {
        const countKey = '{0}';
        let title = alertType.textTemplateSingle.replace(countKey, alertType.alertCount);

        if (alertType.alertCount > 1) {
            title = alertType.textTemplateMultiple.replace(countKey, alertType.alertCount);
        }

        let iconColour = '';

        if (alertType.iconColour?.toLowerCase() === 'concern') {
            iconColour = 'FFC107';
        } else if (alertType.iconColour?.toLowerCase() === 'warning') {
            iconColour = 'FF7454';
        } else if (alertType.iconColour?.toLowerCase() === 'danger') {
            iconColour = 'EC2D6D';
        }

        return <span><i className={classNames('fa', alertType.icon, 'alert-type-icon')} style={{ color: iconColour ? `#${iconColour}` : 'inherit' }} /> {title}</span>;
    }

    const menu = (
        <Menu>
            <InternalMenuItem text="Profile & Settings" location="/user/profile" />
            <MenuDivider />
            <InternalMenuItem text="Beta Features" location="/beta-features" />
            <MenuDivider />
            <MenuItem text="Leave Feedback" onClick={onLeaveFeedback} />
            <MenuItem text="Report an Issue" onClick={onReportIssue} />
            <MenuDivider />
            <MenuItem text="Logout" onClick={onLogout} />
        </Menu>);

    return (
        <div id="top-bar" className={props.environment}>
            <div className="bar-layout">
                <div>
                    <img alt={applicationName ?? "Vision"} id="logo" src={logoSrc} />
                </div>
                <div className="bar-right-items">
                    <div className="top-bar-item">
                        <ShowHide
                            evaluator={props.environment !== ''}
                            show={(
                                <div className="top-bar-item environment-identifier">
                                    <span>{props.environment} environment</span>
                                </div>
                            )}
                        />
                    </div>

                    <div className="top-bar-item">
                        <div className='alert-container inline-items'>
                            <ShowHide
                                evaluator={props.onStop && props.onStop === true}
                                show={(
                                    <div className="inline-item">
                                        <NotificationInline
                                            intent={Intent.WARNING}
                                            allowClose={false}
                                            text='Account is on stop or within 30 days to Renewal'
                                            show={true}
                                            isBanner={true} />
                                    </div>
                                )}
                            />

                            <ShowHide
                                evaluator={canSeeHomePage}
                                show={
                                    <div className="inline-item">
                                        <ButtonList
                                            id="holding-area-menu"
                                            minimal={true}
                                            large={false}
                                            text={<CustomIcon iconName="holding-area" className="holding-area-icon" />}
                                            disabled={loadingAssetHoldingArea || loadingEmployeeHoldingArea}
                                            menuItems={(
                                                <Menu>
                                                    <Fragment>
                                                        <InternalMenuItem
                                                            nodeType='a'
                                                            forceRedirect={true}
                                                            text={<span><i className={classNames('fa', 'fa-user', 'alert-type-icon')} style={{ color: employeeHoldingAreaCount > 0 ? '#EC2D6D' : '' }} /> {employeeHoldingAreaCount} new employee{employeeHoldingAreaCount === 1 ? '' : 's'}</span>}
                                                            location={`${window.env.VISION_URL}#/Alerts/NEWDRIVER`}
                                                            className="bp6-menu-item" />
                                                        <MenuDivider />
                                                        <InternalMenuItem
                                                            nodeType='a'
                                                            forceRedirect={true}
                                                            text={<span><i className={classNames('fa', 'fa-truck', 'alert-type-icon')} style={{ color: assetHoldingAreaCount > 0 ? '#EC2D6D' : '' }} /> {assetHoldingAreaCount} new asset{assetHoldingAreaCount === 1 ? '' : 's'}</span>}
                                                            location={`${window.env.VISION_URL}#/Alerts/NEWVEHICLE`}
                                                            className="bp6-menu-item" />
                                                    </Fragment>
                                                </Menu>)}
                                        />
                                        <ShowHide
                                            evaluator={!loadingAssetHoldingArea && !loadingEmployeeHoldingArea && employeeHoldingAreaCount + assetHoldingAreaCount > 0}
                                            show={
                                                <Tag intent={Intent.DANGER} className="alert-count">
                                                    {employeeHoldingAreaCount + assetHoldingAreaCount > maxAlertCount ? '999+' : employeeHoldingAreaCount + assetHoldingAreaCount}
                                                </Tag>
                                            }
                                        />
                                        <ShowHide
                                            evaluator={loadingAssetHoldingArea || loadingEmployeeHoldingArea}
                                            show={
                                                <div className="alert-count-load">
                                                    <Icon icon="refresh" size={12} />
                                                </div>
                                            }
                                        />
                                    </div>
                                }
                            />
                            <div className="inline-item">
                                <ButtonList
                                    id="alert-bell-menu"
                                    popoverClassName="alert-bell-menu"
                                    minimal={true}
                                    large={false}
                                    text={<i className='fa fa-bell alert-bell-icon' />}
                                    disabled={loadingAlerts}
                                    menuItems={(
                                        <Menu>
                                            {alertTypes.map(x => {
                                                return (
                                                    <Fragment key={x.alertTypeId}>
                                                        <InternalMenuItem
                                                            nodeType={x.storedInLegacySystem ? 'a' : 'MenuItem'}
                                                            forceRedirect={true}
                                                            text={x.formattedTitle}
                                                            location={x.url}
                                                            className="bp6-menu-item" />
                                                        <MenuDivider />
                                                    </Fragment>
                                                );
                                            })}
                                            <InternalMenuItem text={'View all alerts'} location='/alert-type' className='bold' />
                                        </Menu>)}
                                />
                                <ShowHide
                                    evaluator={!loadingAlerts && alertCount > 0}
                                    show={
                                        <Tag intent={Intent.DANGER} className="alert-count">
                                            {alertCount > maxAlertCount ? '999+' : alertCount}
                                        </Tag>
                                    }
                                />
                                <ShowHide
                                    evaluator={loadingAlerts}
                                    show={
                                        <div className="alert-count-load">
                                            <Icon icon="refresh" size={12} />
                                        </div>
                                    }
                                />
                            </div>

                        </div>
                    </div>
                    <div className="top-bar-item">
                        <UserAccess perform="Account:Switch"
                            yes={() => (
                                <Button text={`${accountName} - ${memberNumber}`} minimal={true} large={false} onClick={showAccountSelector} />
                            )}
                            no={() => (
                                <Fragment>
                                    {accountName} - {memberNumber}
                                </Fragment>
                            )}
                        />
                    </div>
                    <div className="top-bar-item">
                        <ButtonList text={(<Fragment>{displayName}  <Icon icon="chevron-down" /></Fragment>)} id="user-menu" icon="person" minimal={true} large={false} menuItems={menu} />
                    </div>
                </div>
            </div>
            <AccountSwitcher isOpen={showAccountSwitcher} onClose={hideAccountSelector}>
                <Fragment>
                    <div className="form-field-full">
                        <FormSuggest
                            loading={accountsLoading}
                            disabled={false}
                            items={switchableAccounts}
                            onItemSelect={onAccountSelect}
                            placeholder="Select account"
                            headingText="Please select which account you would like to switch to:"
                            selectedValue={selectedAccount}
                        />
                    </div>
                    <ButtonSave text="Confirm" icon={null} disabled={attemptingSwitch} loading={accountsLoading} intent="primary" onClick={confirmAccountSwitch} />
                </Fragment>
            </AccountSwitcher>
        </div>
    );
}

TopBar.propTypes = {
    environment: PropTypes.string,
    onStop: PropTypes.bool,
    onLeaveFeedback: PropTypes.func,
    onReportIssue: PropTypes.func
};